/**
 * FLX hero — a direct port of the live site's sticky, scroll-driven stage
 * (https://flx-website-production.up.railway.app/).
 *
 * A 220vh runway with a 100vh `position: sticky` inner stage. The video is an
 * ambient autoplay loop (NOT scrubbed — nothing touches `currentTime`); scroll
 * progress instead drives a parallax zoom on the film, a fade-up on the headline
 * and sub-CTA, a pulsing radial glow, the two pouches sliding in from the sides
 * with a slight counter-rotation, and a staggered reveal of the four corner
 * stats. Timings, easings and magic numbers are the live site's, verbatim.
 *
 * `.flx-root` MUST NOT be `overflow-x: hidden` — that makes it a scroll container
 * and re-anchors this `sticky` to the element rather than the viewport, so the
 * stage scrolls away mid-choreography. `flx.css` uses `clip` for exactly this.
 */
import { useEffect, useRef } from 'react';
import { ImageSlot } from './interactive';
import { FLAVOUR_ORDER, HERO, HERO_STATS, HERO_VIDEO, IMG } from './tokens';

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeIO = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export function Hero() {
  const heroRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const prodLRef = useRef<HTMLElement>(null);
  const prodRRef = useRef<HTMLElement>(null);
  const statRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const render = (p: number) => {
      const video = videoRef.current;
      if (video) {
        const z = 1.06 + easeIO(clamp(p, 0, 1)) * 0.12;
        video.style.transform = `scale(${z.toFixed(4)}) translateY(${(p * -46).toFixed(1)}px)`;
      }
      if (copyRef.current) {
        copyRef.current.style.opacity = String(clamp(1 - p / 0.28, 0, 1));
        copyRef.current.style.transform = `translateY(${(-p * 56).toFixed(1)}px)`;
      }
      if (subRef.current) {
        subRef.current.style.opacity = String(clamp(1 - p / 0.2, 0, 1));
        subRef.current.style.transform = `translateY(${(p * 40).toFixed(1)}px)`;
      }
      if (hintRef.current) hintRef.current.style.opacity = String(clamp(1 - p / 0.06, 0, 1));
      if (glowRef.current) {
        const s = 0.7 + easeIO(clamp(p / 0.6, 0, 1)) * 0.5;
        glowRef.current.style.transform = `translate(-50%,-50%) scale(${s.toFixed(3)})`;
        glowRef.current.style.opacity = (0.5 + s * 0.4).toFixed(2);
      }
      // Pouches: slide in 0.26→0.50, hold, then release near the end of the runway.
      const pt = easeIO(clamp((p - 0.26) / 0.24, 0, 1));
      const pout = clamp(1 - (p - 0.8) / 0.16, 0, 1);
      const pv = (pt * pout).toFixed(3);
      const pouch = (el: HTMLElement | null, dir: 1 | -1) => {
        if (!el) return;
        el.style.opacity = pv;
        el.style.transform =
          `translateX(${lerp(90 * dir, 0, pt).toFixed(1)}px) ` +
          `translateY(${lerp(30, 0, pt).toFixed(1)}px) ` +
          `rotate(${lerp(5 * dir, 0, pt).toFixed(2)}deg)`;
      };
      pouch(prodLRef.current, -1);
      pouch(prodRRef.current, 1);
      statRefs.current.forEach((el, i) => {
        if (!el) return;
        const t = clamp((p - (0.3 + i * 0.06)) / 0.14, 0, 1);
        const out = clamp(1 - (p - 0.82) / 0.18, 0, 1);
        el.style.opacity = (t * out).toFixed(3);
        el.style.transform = `translateY(${lerp(18, 0, t).toFixed(1)}px)`;
      });
    };

    // Reduced motion: compose the finished stage once and never animate. Pinning
    // is pure CSS so the stage still holds; it simply doesn't move.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      render(0.4);
      videoRef.current?.pause();
      return;
    }

    let raf = 0;
    let last = -1;
    const tick = () => {
      const rect = hero.getBoundingClientRect();
      const total = hero.offsetHeight - window.innerHeight;
      const p = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;
      if (Math.abs(p - last) > 0.0003) {
        render(p);
        last = p;
      }
      raf = requestAnimationFrame(tick);
    };
    render(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const [orange, lemon] = FLAVOUR_ORDER;

  return (
    <section id="hero" className="hero" ref={heroRef}>
      <div className="hero__sticky">
        <div className="hero__grid" />
        <div className="hero__bgword">FLX</div>
        <div className="hero__glow" ref={glowRef} />

        <div className="hero__video-wrap">
          <video
            ref={videoRef}
            src={HERO_VIDEO}
            poster={IMG.heroPoster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="FLX Creatine + Electrolytes product showcase"
          />
        </div>

        <div className="hero__scrim" />

        <div className="hero__copy" ref={copyRef}>
          <div className="hero__kicker">
            <span className="bar" />
            {HERO.kicker}
            <span className="bar" />
          </div>
          <h1>
            {HERO.headline[0]}
            <br />
            <span className="accent">{HERO.headline[1]}</span> {HERO.headline[2]}
          </h1>
        </div>

        <div className="hero__products" aria-hidden="true">
          {[
            { f: orange!, ref: prodLRef, mod: 'l' },
            { f: lemon!, ref: prodRRef, mod: 'r' },
          ].map(({ f, ref, mod }) => (
            <figure
              key={f.sku}
              ref={ref as React.RefObject<HTMLElement>}
              className={`hero__product hero__product--${mod}`}
              style={{ ['--fx' as string]: f.hex }}
            >
              <ImageSlot src={f.pouch} label={`${f.flavour} pouch`} />
              <figcaption>
                <span className="hp-flavor">{f.flavour}</span>
                <span className="hp-line">{f.heroCaption}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="hero__stats">
          {HERO_STATS.map((s, i) => (
            <div
              key={s.k}
              className={`hero__stat hero__stat--${'abcd'[i]}`}
              ref={(el) => {
                statRefs.current[i] = el;
              }}
            >
              <div className="line" />
              <div className="v">
                {s.v}
                {s.sup && <span style={{ fontSize: '0.55em', opacity: 0.6 }}>{s.sup}</span>}
              </div>
              <div className="k">{s.k}</div>
            </div>
          ))}
        </div>

        <div className="hero__sub-overlay" ref={subRef}>
          <p>{HERO.body}</p>
          <div className="hero__cta-row">
            <a href={HERO.primaryCta.href} className="btn btn--primary">
              {HERO.primaryCta.label} <span className="arrow">→</span>
            </a>
            <a href={HERO.ghostCta.href} className="btn btn--ghost">
              {HERO.ghostCta.label}
            </a>
          </div>
        </div>

        <div className="scroll-hint" ref={hintRef}>
          <div className="mouse" />
          <span>{HERO.scroll}</span>
        </div>
      </div>
    </section>
  );
}
